import { ResyncSchema, SYNC, type Resync, type SyncRequest, type SyncResponse } from "@station-keeping/contracts";
import { SIM_VERSION, type Action } from "@station-keeping/sim";
import { ApiError, isTransient } from "../api/errors";
import type { Timed } from "../api/client";
import type { GameEngine } from "../game/engine";
import { nextBatch } from "./batch";
import type { ServerClock } from "./clock";

/** Actions qui partent sans attendre : elles coûtent ou changent la station (la mise en veille attend). */
const IMPORTANT: ReadonlySet<Action["type"]> = new Set(["BUILD", "DEMOLISH", "PATCH", "REPAIR", "EXTEND_RING"]);

/** Délai avant de renvoyer un lot resté sans réponse : 2 s, 4 s, 8 s… jusqu'à 30 s. */
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 30_000;

export type SyncStatus = "online" | "offline";

export interface SyncHooks {
  /** Envoi d'un lot (l'API, remplaçable dans les tests). */
  send(request: SyncRequest, keepalive: boolean): Promise<Timed<SyncResponse>>;
  /** L'état du serveur a été repris (conflit, écart, action refusée) : la file locale est abandonnée. */
  onResync(resync: Resync, reason: string): void;
  /** Un autre appareil a pris la main sur la station. */
  onTakenOver(): void;
  /** Le serveur a refusé sans joindre son état : il faut rouvrir une session. */
  onReopen(reason: string): void;
  /** Erreur qui arrête la partie (session perdue, compte suspendu, app trop ancienne, maintenance). */
  onFatal(error: ApiError): void;
  onStatus(status: SyncStatus): void;
  newBatchId(): string;
}

/**
 * La sauvegarde en continu. Les actions partent par lots (1,5 s après la dernière, au tick suivant pour une
 * action importante), un battement part toutes les 30 s, une seule requête à la fois. Un lot sans réponse est
 * renvoyé tel quel (même `batchId`, le serveur le reconnaît). Un refus du serveur vient avec son état, que le
 * jeu reprend.
 */
export class SyncManager {
  private running = false;
  private leaseId = "";
  private revision = 0;
  private verifiedTick = 0;
  /** Lot envoyé, gardé jusqu'à sa réponse : c'est lui qui repart après une coupure. */
  private pending: SyncRequest | null = null;
  private inFlight: Promise<void> | null = null;
  private again = false;
  private failures = 0;
  private status: SyncStatus = "online";
  private heartbeatOn = true;
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;
  private soonTimer: ReturnType<typeof setTimeout> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  private unsubscribe: (() => void) | null = null;

  constructor(
    private readonly engine: GameEngine,
    private readonly clock: ServerClock,
    private readonly hooks: SyncHooks,
  ) {}

  get isRunning(): boolean {
    return this.running;
  }

  /** Démarre avec le bail et la révision d'une session tout juste ouverte. */
  start(session: { leaseId: string; revision: number; tick: number }): void {
    this.stop();
    this.running = true;
    this.leaseId = session.leaseId;
    this.revision = session.revision;
    this.verifiedTick = session.tick;
    this.failures = 0;
    this.setStatus("online");
    this.unsubscribe = this.engine.onAction(({ action }) => {
      if (IMPORTANT.has(action.type)) this.soon();
      else this.debounce();
    });
    this.heartbeatTimer = setInterval(() => {
      if (this.heartbeatOn && !this.inFlight && !this.retryTimer) void this.flush();
    }, SYNC.heartbeatSeconds * 1000);
  }

  /** Arrête tout (reprise par un autre appareil, déconnexion) ; une réponse en route sera ignorée. */
  stop(): void {
    this.running = false;
    this.pending = null;
    this.again = false;
    this.unsubscribe?.();
    this.unsubscribe = null;
    clearTimeout(this.debounceTimer);
    clearTimeout(this.soonTimer);
    clearTimeout(this.retryTimer);
    clearInterval(this.heartbeatTimer);
    this.debounceTimer = undefined;
    this.soonTimer = undefined;
    this.retryTimer = undefined;
    this.heartbeatTimer = undefined;
  }

  /** Battement en pause (onglet caché : le temps du serveur compte l'absence, pas un tick figé). */
  setHeartbeat(on: boolean): void {
    this.heartbeatOn = on;
  }

  private debounce(): void {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = undefined;
      void this.flush();
    }, SYNC.debounceMs);
  }

  /** Au tick suivant : une action n'entre dans un lot qu'une fois son tick passé. */
  private soon(): void {
    if (this.soonTimer) return;
    this.soonTimer = setTimeout(() => {
      this.soonTimer = undefined;
      void this.flush();
    }, this.engine.msToNextTick() + 50);
  }

  /** Envoie ce qui attend : un lot d'actions, ou un simple battement. */
  flush(options: { keepalive?: boolean } = {}): Promise<void> {
    if (!this.running) return Promise.resolve();
    const keepalive = options.keepalive ?? false;
    if (this.inFlight) {
      // Page qui se ferme : la requête en route risque d'être coupée, sa copie part quand même (même lot).
      if (keepalive && this.pending) void this.hooks.send(this.pending, true).catch(() => undefined);
      else this.again = true;
      return this.inFlight;
    }
    clearTimeout(this.debounceTimer);
    clearTimeout(this.retryTimer);
    this.debounceTimer = undefined;
    this.retryTimer = undefined;

    // L'état doit être à jour avant de dater le lot (le rendu peut avoir pris du retard).
    this.engine.advance();
    if (!this.running) return Promise.resolve();
    const request = this.pending ?? this.buildRequest();
    if (!request) return Promise.resolve();
    this.pending = request;
    this.inFlight = this.exchange(request, keepalive).finally(() => {
      this.inFlight = null;
      const again = this.again;
      this.again = false;
      // Après un échec, c'est le renvoi programmé qui repart (sans court-circuiter l'attente).
      if (this.running && again && !this.retryTimer) void this.flush();
    });
    return this.inFlight;
  }

  private buildRequest(): SyncRequest | null {
    const boundary = this.engine.boundaryHash();
    if (boundary.tick < this.verifiedTick) return null;
    const batch = nextBatch(this.engine.outbox, boundary, this.engine.boundaries, SYNC.maxActionsPerSync);
    return {
      leaseId: this.leaseId,
      revision: this.revision,
      batchId: this.hooks.newBatchId(),
      simVersion: SIM_VERSION,
      tick: batch.tick,
      hash: batch.hash,
      actions: batch.actions,
    };
  }

  private async exchange(request: SyncRequest, keepalive: boolean): Promise<void> {
    let response: Timed<SyncResponse>;
    try {
      response = await this.hooks.send(request, keepalive);
    } catch (error) {
      if (this.running && this.pending === request) this.failed(error);
      return;
    }
    const { data, sentAt, receivedAt } = response;
    this.clock.sample(data.serverTime, sentAt, receivedAt);
    if (!this.running || this.pending !== request) return;
    this.pending = null;
    this.failures = 0;
    this.setStatus("online");
    this.revision = data.revision;
    this.verifiedTick = data.verifiedTick;

    if (data.resync) {
      // Autre version de la simulation : le serveur fait foi à chaque lot ; les actions jouées depuis
      // l'envoi sont rejouées sur son état.
      this.adopt(data.resync, "server_truth", request);
      return;
    }
    const last = request.actions.at(-1);
    this.engine.acknowledge(last ? last.seq : 0, request.tick);
    // Des actions sont arrivées pendant l'échange (ou attendaient la fin de leur tick).
    if (this.engine.outbox.length > 0) this.debounce();
  }

  private failed(error: unknown): void {
    if (isTransient(error)) {
      // Même lot, plus tard : le serveur reconnaîtra son `batchId` s'il l'a déjà traité.
      this.failures += 1;
      if (!(error instanceof ApiError)) this.setStatus("offline");
      const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (this.failures - 1));
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => {
        this.retryTimer = undefined;
        void this.flush();
      }, delay);
      return;
    }
    this.pending = null;
    if (!(error instanceof ApiError)) {
      this.stop();
      this.hooks.onReopen("unexpected");
      return;
    }
    if (error.status === 409) {
      if (error.code === "STATION_TAKEN_OVER") {
        this.stop();
        this.hooks.onTakenOver();
        return;
      }
      const resync = ResyncSchema.safeParse(error.details?.resync);
      if (resync.success) {
        this.adopt(resync.data, error.code, null);
        return;
      }
      this.stop();
      this.hooks.onReopen(error.code);
      return;
    }
    if (error.status === 400) {
      // Lot refusé tel quel (ne devrait pas arriver) : on repart de l'état du serveur.
      this.stop();
      this.hooks.onReopen(error.code);
      return;
    }
    this.stop();
    this.hooks.onFatal(error);
  }

  /** Reprend l'état du serveur ; `sent` : le lot accepté dont les actions sont déjà dans cet état. */
  private adopt(resync: Resync, reason: string, sent: SyncRequest | null): void {
    const lastSent = sent?.actions.at(-1)?.seq ?? 0;
    const replay = sent ? this.engine.outbox.filter((scheduled) => scheduled.seq > lastSent).map((scheduled) => scheduled.action) : [];
    this.revision = resync.revision;
    this.verifiedTick = resync.tick;
    this.engine.reset(resync.state, { tick: resync.anchor.tick, at: Date.parse(resync.anchor.serverTime) }, resync.lastSeq);
    for (const action of replay) this.engine.dispatch(action);
    this.hooks.onResync(resync, reason);
  }

  private setStatus(status: SyncStatus): void {
    if (status === this.status) return;
    this.status = status;
    this.hooks.onStatus(status);
  }
}
